import * as Sentry from "@sentry/react";
import React from "react";
import ReactDOM from "react-dom/client";
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router-dom";
import { App } from "./components/App";
import { GlobalError } from "./pages/GlobalError";
import * as serviceWorkerRegistration from "./serviceWorkerRegistration";
import { connect } from "./ws";
import { getSessionId } from "./utils/observability";

// Defer Sentry init minimally to avoid blocking first paint
const initSentry = () =>
  Sentry.init({
    release: process.env.REACT_APP_VERSION,
    dsn: "https://d5fc6923c6b84efda702a8bb433659e9@o354606.ingest.sentry.io/6228391",
    environment: process.env.REACT_APP_SENTRY_ENV || process.env.NODE_ENV,
    enableMetrics: true,
    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect: React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.replayIntegration({
        // Bodies are costly on resource-limited kiosks
        networkCaptureBodies: false,
        networkDetailAllowUrls: [/expedibox/i, /127\.0\.0\.1:5000/, /127\.0\.0\.1:5001/],
        blockAllMedia: true,
        maskAllText: false,
        maskAllInputs: false,
        maxReplayDuration: 30000, // 30 seconds
      }),
    ],
    // Much lower sampling in production to reduce CPU/memory/network
    replaysOnErrorSampleRate: process.env.NODE_ENV === "production" ? 0 : 0.5,
    replaysSessionSampleRate: process.env.NODE_ENV === "production" ? 0 : 0.05,
    tracesSampleRate: process.env.NODE_ENV === "production" ? 0.01 : 0.2,
    maxBreadcrumbs: 25,
    transport: Sentry.makeBrowserOfflineTransport(Sentry.makeFetchTransport),
  });

if ("requestIdleCallback" in window) {
  (window as any).requestIdleCallback(() => initSentry());
} else {
  setTimeout(() => initSentry(), 0);
}

Sentry.setContext("locker", {
  lockerId: localStorage.getItem("lockerId"),
});

Sentry.setTag("locker", localStorage.getItem("lockerId"));
Sentry.setTag("session", getSessionId());

const root = ReactDOM.createRoot(document.getElementById("root") as HTMLElement);
root.render(
  <React.StrictMode>
    <Sentry.ErrorBoundary fallback={<GlobalError />}>
      <App />
    </Sentry.ErrorBoundary>
  </React.StrictMode>
);

serviceWorkerRegistration.register({
  onUpdate: (registration) => {
    const waiting = registration.waiting;

    if (waiting) {
      waiting.addEventListener("statechange", (event) => {
        const target = event.target as ServiceWorker;
        if (target.state === "activated") {
          window.location.reload();
        }
      });
      waiting.postMessage({ type: "SKIP_WAITING" });
    }
  },
});

connect();
